import React, { useState, useEffect } from 'react';

const EditProductVariantModal = ({ 
  isOpen, 
  onClose, 
  variantAttributes,
  toggleVariantValue,
  addNewVariants // New function to add variants instead of replacing them
}) => {
  const [selectedAttributeIds, setSelectedAttributeIds] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState([]);

  
  // Reset selected variants when modal opens and expand all attributes
  useEffect(() => {
    if (isOpen) {
      setSelectedVariants([]);
      // Auto expand all attribute sections
      setSelectedAttributeIds(variantAttributes.map(attr => attr.id));
    }
  }, [isOpen, variantAttributes]);

  const handleAttributeToggle = (attributeId) => {
    setSelectedAttributeIds(prev => {
      if (prev.includes(attributeId)) {
        return prev.filter(id => id !== attributeId);
      } else {
        return [...prev, attributeId];
      }
    });
  };

  const isVariantValueSelected = (attributeId, valueId) => {
    return selectedVariants.some(
      item => item.attributeId === attributeId && item.valueId === valueId
    );
  };

  const localToggleVariantValue = (attributeId, valueId) => {
    const variantKey = { attributeId, valueId };
    
    setSelectedVariants(prev => {
      const exists = prev.some(item => 
        item.attributeId === attributeId && item.valueId === valueId
      );

      if (exists) {
        return prev.filter(item => 
          !(item.attributeId === attributeId && item.valueId === valueId)
        );
      } else {
        return [...prev, variantKey];
      }
    });
  };

  const handleGenerateVariants = () => {
    // Don't add barCode here since selectedVariants are just attribute-value pairs
    // The actual variant generation happens in the parent component
    
    // Call the new function to add variants instead of replacing them
    addNewVariants(selectedVariants); // Keep original selectedVariants
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
          <div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                Configurer les variantes du produit
              </h3>
              <div className="mt-2 text-sm text-gray-500">
                <p>
                  Sélectionnez les attributs et valeurs pour générer de nouvelles variantes du produit. 
                  <span className="font-semibold"> Les variantes existantes seront conservées.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Add fixed height with overflow for scrolling content */}
          <div className="mt-6" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="grid grid-cols-1 gap-6">
              {variantAttributes.map(attribute => (
                <div key={attribute.id} className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex items-center justify-between cursor-pointer"
                       onClick={() => handleAttributeToggle(attribute.id)}>
                    <div>
                      <h3 className="text-base leading-6 font-medium text-gray-900">
                        {attribute.name}
                      </h3>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Sélectionnez une ou plusieurs options
                      </p>
                    </div>
                    <div>
                      <svg className={`h-5 w-5 text-gray-400 transform ${selectedAttributeIds.includes(attribute.id) ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  
                  {selectedAttributeIds.includes(attribute.id) && (
                    <div className="px-4 py-5 sm:px-6">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {attribute.values.map(value => (
                          <div key={value.id} className="relative flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id={`value-${value.id}`}
                                type="checkbox"
                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                checked={isVariantValueSelected(attribute.id, value.id)}
                                onChange={() => localToggleVariantValue(attribute.id, value.id)}
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor={`value-${value.id}`} className="font-medium text-gray-700 cursor-pointer">
                                {value.value}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Fixed footer with buttons */}
          <div className="mt-8 pt-4 border-t border-gray-200 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#174540] text-base font-medium text-white hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:col-start-2 sm:text-sm"
              onClick={handleGenerateVariants}
            >
              Ajouter des variantes
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:col-start-1 sm:text-sm"
              onClick={onClose}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProductVariantModal;